import { Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ForcePaymentCheckDialog from '../../components/invoices/ForcePaymentCheckDialog'
import CustomContainer from '../../components/common/CustomContainer'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { RouteConstants } from '../../navigation/navigation-types'
import { GetExternalDashboardDataResponse } from '../../services/api/collections/dashboard/getExternalDashboardData/getExternalDashboardData_200'
import { GetInternalDashboardDataResponse } from '../../services/api/collections/dashboard/getInternalDashboardData/getInternalDashboardData_200'
import { ContractorManager } from '../../services/api/ContractorManager'
import { DashboardManager } from '../../services/api/DashboardManager'
import { EmailManager } from '../../services/api/EmailManager'
import { OrderState } from '../../services/api/sharedTypes/Common'
import { EmailMessageState } from '../../services/api/sharedTypes/Email'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import WaitingEmailsTable from '../emailsAndSurveys/tables/WaitingEmailsTable'
import NoData from './NoData'
import NewlyFilledSurveysTable from './tables/NewlyFilledSurveysTable'
import NotPaidContractorsTable from './tables/NotPaidContractorsTable'
import PendingDemandsTable from './tables/PendingDemandsTable'
import PlannedEventsTable from './tables/PlannedEventsTable'
import UnpaidInvoicesTable from './tables/UnpaidInvoicesTable'

const Overview: React.FC = () => {
    const dispatch = useAppDispatch()
    const theme = useTheme()

    const [loadingInternalData, setLoadingInternalData] = useState(false)
    const [loadingExternalData, setLoadingExternalData] = useState(false)
    const [internalDashBoard, setInternalDashBoard] = useState<GetInternalDashboardDataResponse>()
    const [externalDashBoard, setExternalDashBoard] = useState<GetExternalDashboardDataResponse>()
    const [forceCheckOpen, setForceCheckOpen] = useState(false)

    const fetchInternalData = async () => {
        setLoadingInternalData(true)

        try {
            const dashRes = await DashboardManager.getManager().getInternalDashboardDataResponse()
            if (dashRes) {
                setInternalDashBoard(dashRes)
            }
        } catch (error) {
            setLoadingInternalData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }

        setLoadingInternalData(false)
    }

    useEffect(() => {
        const fetchExternalData = async () => {
            setLoadingExternalData(true)

            try {
                const dashRes = await DashboardManager.getManager().getExternalDashboardDataResponse()
                if (dashRes) {
                    setExternalDashBoard(dashRes)
                }
            } catch (error) {
                setLoadingInternalData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }

            setLoadingExternalData(false)
        }

        fetchInternalData()
        fetchExternalData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const updateEmailMessage = async (id: string, subject: string, emailBody: string, updateAndSendEmailMessage?: boolean) => {
        if (!internalDashBoard) return
        try {
            const res = await EmailManager.getManager().updateEmailMessageAsync(id, subject, emailBody)
            if (res) {
                setInternalDashBoard({
                    ...internalDashBoard,
                    emailsToSend: internalDashBoard.emailsToSend.map((email) => (email.id === id ? res : email)),
                })
                if (updateAndSendEmailMessage) {
                    return sendEmailMessage(id)
                }
                return dispatch(displayMessage({ message: 'Email byl úspěšně upraven', type: 'success' }))
            }
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const deleteEmailMessage = async (id: string) => {
        try {
            await EmailManager.getManager().deleteEmailMessageAsync(id)
            const newEmails = internalDashBoard?.emailsToSend.filter((item) => item.id !== id)
            setInternalDashBoard({ ...(internalDashBoard as GetInternalDashboardDataResponse), emailsToSend: newEmails ?? [] })
            dispatch(displayMessage({ message: 'Email byl úspěšně smazán', type: 'success' }))
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const sendEmailMessage = async (id: string) => {
        if (!internalDashBoard) return
        try {
            await EmailManager.getManager().sendEmailMessageAsync(id)
            setInternalDashBoard({
                ...internalDashBoard,
                emailsToSend: internalDashBoard.emailsToSend.map((email) =>
                    email.id === id ? { ...email, state: EmailMessageState.Sent } : email
                ),
            })
            dispatch(displayMessage({ message: 'Email byl úspěšně odeslán', type: 'success' }))
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const payContractor = async (contractorId: string, eventId: string) => {
        try {
            await ContractorManager.getManager().payContractorAsync(contractorId, eventId)
            dispatch(displayMessage({ message: 'Platba byla úspěšná', type: 'success' }))
            setInternalDashBoard((prev) => {
                if (prev) {
                    const newContractors = prev.notPaidContractors.filter((c) => c.contractorIdName.id !== contractorId)
                    return { ...prev, notPaidContractors: newContractors }
                }
                return prev
            })
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    if (loadingInternalData) return <ScreenLoadingPlaceholder />

    if (!internalDashBoard) return <Typography variant='h4'>Data nenačtena</Typography>

    return (
        <>
            <CustomContainer bgColor={theme.palette.background.default} disablePadding>
                <Typography variant='h5' marginX={3} marginTop={2}>
                    Dashboard
                </Typography>
            </CustomContainer>
            <CustomContainer>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='h6' color='primary'>
                        Nezaplacené faktury
                    </Typography>
                    <Button variant='outlined' size='small' onClick={() => setForceCheckOpen(true)}>
                        Zkontrolovat platby
                    </Button>
                </Stack>
                {internalDashBoard.unpaidInvoices.length > 0 ? (
                    <UnpaidInvoicesTable unpaidInvoices={internalDashBoard.unpaidInvoices} />
                ) : (
                    <Typography variant='caption'>Žádný záznam</Typography>
                )}
            </CustomContainer>
            <ForcePaymentCheckDialog
                open={forceCheckOpen}
                onClose={() => setForceCheckOpen(false)}
                onSuccess={fetchInternalData}
            />
            <CustomContainer>
                <Typography variant='h6' color='primary'>
                    Nevyřízené poptávky
                </Typography>
                {internalDashBoard.pendingDemands.length > 0 ? (
                    <PendingDemandsTable pendingDemands={internalDashBoard.pendingDemands} />
                ) : (
                    <NoData link={RouteConstants.events} />
                )}
            </CustomContainer>
            <CustomContainer>
                <Typography variant='h6' color='primary'>
                    Emaily
                </Typography>
                {internalDashBoard.emailsToSend.length > 0 ? (
                    <WaitingEmailsTable
                        onDelete={deleteEmailMessage}
                        onUpdate={updateEmailMessage}
                        emails={internalDashBoard.emailsToSend.filter(
                            (email) => email.state === EmailMessageState.WaitingToBeSent
                        )}
                    />
                ) : (
                    <NoData link={RouteConstants.emailsAndSurveys} />
                )}
            </CustomContainer>
            <CustomContainer>
                <Typography variant='h6' color='primary'>
                    Čeká k zaplacení
                </Typography>
                {internalDashBoard.notPaidContractors.filter((contractor) => contractor.orderState === OrderState.Finished)
                    .length > 0 ? (
                    <NotPaidContractorsTable
                        notPaidContractors={internalDashBoard.notPaidContractors.filter(
                            (contractor) => contractor.orderState === OrderState.Finished
                        )}
                        payContractor={payContractor}
                    />
                ) : (
                    <NoData link={RouteConstants.clients} />
                )}
            </CustomContainer>
            <CustomContainer>
                <Typography variant='h6' color='primary'>
                    Akce k plánování
                </Typography>
                {internalDashBoard.plannedEvents.length > 0 ? (
                    <PlannedEventsTable plannedEvents={internalDashBoard.plannedEvents} />
                ) : (
                    <NoData link={RouteConstants.events} />
                )}
            </CustomContainer>

            <CustomContainer>
                <Typography variant='h6' color='primary'>
                    Nově vyplněné dotazníky (Posledních 7 dní)
                </Typography>
                {loadingExternalData ? (
                    <CircularProgress />
                ) : !externalDashBoard ? (
                    <Typography variant='h4'>Data nenačtena</Typography>
                ) : externalDashBoard.newlyFilledSurveys.length > 0 ? (
                    <NewlyFilledSurveysTable newlyFilledSurveys={externalDashBoard.newlyFilledSurveys} />
                ) : (
                    <NoData link={RouteConstants.emailsAndSurveys} />
                )}
            </CustomContainer>
        </>
    )
}

export default Overview
